import { Injectable, signal } from '@angular/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { ModalController } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { BiometricModalComponent } from '../modules/login/components/biometric-modal/biometric-modal.component';
import { CustomToastService } from './custom-toast.service';

@Injectable({
  providedIn: 'root'
})
export class BiometricService {

  private serverName = "tqrscanner-biometrics";
  private _cachedUsername: string | null = null;

  public hasBiometricCredentials = signal<boolean>(false);
  public biometricImage = signal<string | null>(null);
  public biometricIcon = signal<string>('finger-print');

  constructor(
    private modalCtrl: ModalController,
    private _logger: NGXLogger,
    private _translate: TranslateService,
    private toast: CustomToastService,
  ) { }

  /**
   * Verifica si la autenticación biométrica está disponible
   */
  async biometricAvailable(): Promise<boolean> {
    try {
      const result = await NativeBiometric.isAvailable();
      return result.isAvailable;
    } catch (error) {
      this._logger.error("Error verificando biometría", error);
      return false;
    }
  }


  /**
  *   Verificación biométrica
  */
  async performBiometricVerification(): Promise<boolean> {
    try {
      return await NativeBiometric.verifyIdentity({
        reason: this._translate.instant('login.biometric.reason'),
        title: this._translate.instant('login.biometric.title'),
        subtitle: this._translate.instant('login.biometric.subtitle'),
      })
        .then(() => true)
        .catch(() => false);
    } catch (error) {
      this._logger.error("Error en autenticación biométrica", error);
      return false;
    }
  }

  /**
   * Guarda las credenciales en biometría
   */
  async saveCredentials(credential: string, password: string): Promise<boolean> {
    const verified = await this.performBiometricVerification();

    if (verified) {
      try {
        await NativeBiometric.setCredentials({
          username: credential,
          password: password,
          server: this.serverName
        });
        this.hasBiometricCredentials.set(true);
        this._logger.info("Credenciales biométricas guardadas exitosamente.");
        this.toast.show(this._translate.instant('login.biometric.save-success'), 3500, 'success');
        return true;
      } catch (error) {
        this._logger.error("Error guardando credenciales biométricas", error);
        this.toast.show(this._translate.instant('login.biometric.save-error'), 3500, 'error');
        return false;
      }
    } else {
      this.toast.show(this._translate.instant('login.biometric.verify-error'), 3500, 'error');
      return false;
    }
  }


  /**
  * Obtiene las credenciales biométricas almacenadas
  */
  async getBiometricCredentials(): Promise<{ username: string, password: string } | null> {
    try {
      const credentials = await NativeBiometric.getCredentials({ server: this.serverName });

      if (!credentials.username || !credentials.password) {
        throw new Error("No se encontraron credenciales biométricas.");
      }

      return credentials;
    } catch (error) {
      this._logger.error("Error obteniendo credenciales biométricas", error);
      return null;
    }
  }

  /**
  * Elimina las credenciales biométricas almacenadas
  */
  async deleteBiometricCredentials(): Promise<boolean> {
    try {
      await NativeBiometric.deleteCredentials({ server: this.serverName });
      this.hasBiometricCredentials.set(false);
      this._logger.info("Credenciales biométricas eliminadas exitosamente.");
      this.toast.show(this._translate.instant('login.biometric.delete-success'), 3500, 'success');

      return true;
    } catch (error) {
      this._logger.error("Error eliminando credenciales biométricas", error);
      this.toast.show(this._translate.instant('login.biometric.delete-error'), 3500, 'error');

      return false;
    }
  }


  /**
  * Muestra el modal para preguntar si el usuario quiere habilitar la biometría
  */
  async askForBiometricLogin(credential: string, password: string): Promise<boolean> {

    if (Capacitor.getPlatform() === 'web') {
      return false;
    }

    const biometricAvailable = await this.biometricAvailable();
    if (!biometricAvailable) {
      return false;
    }

    const modal = await this.modalCtrl.create({
      component: BiometricModalComponent,
      cssClass: 'custom-modal',
      showBackdrop: true,
      backdropDismiss: false,
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data?.enableBiometric) {
      return await this.saveCredentials(credential, password);
    }

    return false;
  }

  public getCachedUsername(): string | null {
    return this._cachedUsername;
  }

  /**
   * Verifica si existen credenciales biométricas guardadas
   */
  async checkBiometricCredentials(): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') {
      this.hasBiometricCredentials.set(false);
      return false;
    }

    try {
      const credentials = await this.getBiometricCredentials();
      const hasCredentials = credentials !== null;

      this.hasBiometricCredentials.set(hasCredentials);
      this._cachedUsername = credentials?.username ?? null;

      return hasCredentials;
    } catch (error) {
      this._logger.error("Error verificando credenciales biométricas", error);
      this.hasBiometricCredentials.set(false);
      this._cachedUsername = null;
      return false;
    }
  }

  /**
  * Devuelve los datos visuales para la autenticación biométrica según la plataforma
  */
  setBiometricUI() {
    if (Capacitor.getPlatform() === 'ios') {
      this.biometricImage.set('assets/images/face-id.png');
      this.biometricIcon.set('');
    } else {
      this.biometricImage.set(null);
      this.biometricIcon.set('finger-print');
    }
  }

  async rebindWithNewCredential(newCredential: string): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') return false;

    try {
      const existing = await this.getBiometricCredentials();
      if (!existing) {
        return false;
      }

      const ok = await this.performBiometricVerification();
      if (!ok) {
        this.toast.show(this._translate.instant('login.biometric.verify-error'), 3500, 'error');
        return false;
      }

      await NativeBiometric.deleteCredentials({ server: this.serverName });

      await NativeBiometric.setCredentials({
        username: newCredential,
        password: existing.password,
        server: this.serverName
      });

      this._cachedUsername = newCredential;
      console.log('Biometría: credenciales actualizadas tras cambio de perfil.', newCredential);
      this.hasBiometricCredentials.set(true);
      this._logger.info('Credenciales biométricas actualizadas tras cambio de perfil.');
      this.toast.show(this._translate.instant('login.biometric.update-success'), 3500, 'success');
      return true;

    } catch (error) {
      this._logger.error('Error re-vinculando biometría', error);
      this.toast.show(this._translate.instant('login.biometric.update-error'), 3500, 'error');
      return false;
    }
  }


  async rebindWithNewPassword(newPassword: string): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') return false;

    try {
      const existing = await this.getBiometricCredentials();
      if (!existing) {
        return false;
      }

      const ok = await this.performBiometricVerification();
      if (!ok) {
        this.toast.show(this._translate.instant('login.biometric.verify-error'), 3500, 'error');
        return false;
      }

      await NativeBiometric.deleteCredentials({ server: this.serverName });

      await NativeBiometric.setCredentials({
        username: existing.username,
        password: newPassword,
        server: this.serverName
      });

      this._cachedUsername = existing.username;
      this.hasBiometricCredentials.set(true);
      this._logger.info('Biometría: contraseña actualizada tras cambio de password.');
      this.toast.show(this._translate.instant('login.biometric.update-success-password'), 3500, 'success');
      return true;

    } catch (error) {
      this._logger.error('Error actualizando password en biometría', error);
      this.toast.show(this._translate.instant('login.biometric.update-error'), 3500, 'error');
      return false;
    }
  }


}
